// The "references" wall: client logos scrolling as one continuous strip.
//
// Renders nothing for zero or one logo — a marquee of a single item reads as a
// glitch, and one reference is weaker social proof than none.

import type { Dict } from "#/lib/i18n";

/** Seconds each logo spends crossing the strip. Slow enough to read a wordmark. */
const SECONDS_PER_LOGO = 3;

export function PartnersMarquee({ partners }: { partners: Dict["partners"] }) {
	const items = partners?.items ?? [];
	if (items.length < 2) return null;

	// Two copies back to back: the CSS translates the track by -50%, so the
	// second copy is exactly where the first began when the loop restarts. The
	// `copy` flag doubles as a stable React key and as the a11y marker — the
	// duplicate is decoration, so screen readers should only meet the list once.
	const loop = [
		...items.map((p) => ({ ...p, copy: 0 })),
		...items.map((p) => ({ ...p, copy: 1 })),
	];

	return (
		<section data-section="partners" className="bg-white py-14 sm:py-16">
			<div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
				<div className="flex flex-col items-center gap-2 text-center">
					<span className="text-xs font-bold uppercase tracking-[0.18em] text-muted">
						{partners.eyebrow}
					</span>
					<h2 className="font-display text-2xl font-bold text-navy sm:text-3xl">
						{partners.heading}
					</h2>
				</div>
			</div>

			<div
				// LTR has to sit on the *scroll container*, not on the track: the
				// container decides which way its over-wide child overflows. Under
				// the Arabic page's RTL the track would start ~10,000px to the left,
				// off-screen, and translating it further left never brings it back.
				dir="ltr"
				className="marquee relative mt-10 overflow-hidden"
				// The logos are white-background tiles, so the fade has to be a real
				// mask rather than a gradient overlay in the page background colour.
				style={{
					maskImage:
						"linear-gradient(to right, transparent, black 6rem, black calc(100% - 6rem), transparent)",
					WebkitMaskImage:
						"linear-gradient(to right, transparent, black 6rem, black calc(100% - 6rem), transparent)",
				}}
			>
				<ul
					className="marquee-track flex w-max items-center gap-4 sm:gap-6"
					style={{
						["--marquee-duration" as string]: `${items.length * SECONDS_PER_LOGO}s`,
					}}
				>
					{loop.map((partner) => (
						<li
							key={`${partner.copy}-${partner.logo}`}
							aria-hidden={partner.copy === 1}
							className="flex h-20 w-40 shrink-0 items-center justify-center rounded-2xl bg-white p-3 ring-1 ring-line sm:h-24 sm:w-48"
						>
							<img
								src={partner.logo}
								alt={partner.name}
								width={320}
								height={160}
								// Not lazy: a tile can sit thousands of pixels off to the
								// side and only translate into view a minute later, which
								// with lazy loading shows an empty card until it does. The
								// whole set is ~130 KB, so it's cheaper to just fetch it.
								loading="eager"
								fetchPriority="low"
								decoding="async"
								className="max-h-full w-auto object-contain"
							/>
						</li>
					))}
				</ul>
			</div>
		</section>
	);
}
